import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { DIVISIONS, PRIORITIES, STATUSES, PLATFORMS, WORK_TYPES, APPROVAL_STATES } from "@/lib/ui";
import { SectionLabel } from "@/components/Bits";

const inputCls =
  "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white focus:ring-1 focus:ring-white transition-colors";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1 block font-medium">{label}</label>
      {children}
    </div>
  );
}

const EMPTY = {
  brand: "", division: "Presence & Listings", title: "", description: "", priority: "Medium",
  assigned_to: "", assigned_by: "", approver: "", start_date: "", due_date: "",
  status: "Not Started", platform: "", work_type: "", sku_count: "", shoot_date: "",
  location: "", crew: "", domain: "", stage: "", client_inputs: "", working_url: "",
  approval_status: "Not Required Yet",
};

export default function TaskFormDialog({ open, onOpenChange, clients, team, onCreated, defaultDivision }) {
  const [form, setForm] = useState({ ...EMPTY, division: defaultDivision || EMPTY.division });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => setForm({ ...EMPTY, division: defaultDivision || EMPTY.division });

  const submit = async () => {
    if (!form.brand || !form.title) {
      toast.error("Brand and Task Title are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, sku_count: form.sku_count ? Number(form.sku_count) : null };
      await api.post("/tasks", payload);
      toast.success("Task created & assigned");
      reset();
      onOpenChange(false);
      onCreated && onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Create & Assign Task</DialogTitle>
          <DialogDescription className="sr-only">Create and assign a new agency task across a service division.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          <div className="space-y-3">
            <SectionLabel>1 · Task Basics</SectionLabel>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Brand / Client *">
                <select data-testid="task-brand-select" className={inputCls} value={form.brand} onChange={(e) => set("brand", e.target.value)}>
                  <option value="">Select brand</option>
                  {clients.map((c) => <option key={c.id} value={c.brand}>{c.brand}</option>)}
                </select>
              </Field>
              <Field label="Service Division *">
                <select data-testid="task-division-select" className={inputCls} value={form.division} onChange={(e) => set("division", e.target.value)}>
                  {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Task Title *">
              <input data-testid="task-title-input" className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Flipkart catalog listing — 50 SKUs" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Priority">
                <select data-testid="task-priority-select" className={inputCls} value={form.priority} onChange={(e) => set("priority", e.target.value)}>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Description / Expected Output">
              <textarea data-testid="task-description-input" className={inputCls} rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} />
            </Field>
          </div>

          <div className="space-y-3">
            <SectionLabel>2 · Assignment & Deadline</SectionLabel>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Assigned To">
                <select data-testid="task-assignee-select" className={inputCls} value={form.assigned_to} onChange={(e) => set("assigned_to", e.target.value)}>
                  <option value="">Unassigned</option>
                  {team.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
                </select>
              </Field>
              <Field label="Approver">
                <select className={inputCls} value={form.approver} onChange={(e) => set("approver", e.target.value)}>
                  <option value="">—</option>
                  {team.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
                </select>
              </Field>
              <Field label="Due Date">
                <input type="date" className={inputCls} value={form.due_date} onChange={(e) => set("due_date", e.target.value)} />
              </Field>
            </div>
          </div>

          <div className="space-y-3">
            <SectionLabel>3 · Service-Specific Details</SectionLabel>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Platform">
                <select className={inputCls} value={form.platform} onChange={(e) => set("platform", e.target.value)}>
                  <option value="">—</option>
                  {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Work Type">
                <select className={inputCls} value={form.work_type} onChange={(e) => set("work_type", e.target.value)}>
                  <option value="">—</option>
                  {WORK_TYPES.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </Field>
              <Field label="No. of Products / SKUs">
                <input type="number" className={inputCls} value={form.sku_count} onChange={(e) => set("sku_count", e.target.value)} />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Shoot Date (Production)">
                <input type="date" className={inputCls} value={form.shoot_date} onChange={(e) => set("shoot_date", e.target.value)} />
              </Field>
              <Field label="Location / Crew">
                <input className={inputCls} value={form.crew} onChange={(e) => set("crew", e.target.value)} placeholder="Studio · Photographer" />
              </Field>
              <Field label="Domain (Website)">
                <input className={inputCls} value={form.domain} onChange={(e) => set("domain", e.target.value)} />
              </Field>
            </div>
          </div>

          <div className="space-y-3">
            <SectionLabel>4 · Client Inputs, Files & Approval</SectionLabel>
            <Field label="Client Inputs Required">
              <input className={inputCls} value={form.client_inputs} onChange={(e) => set("client_inputs", e.target.value)} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Working / Folder URL">
                <input className={inputCls} value={form.working_url} onChange={(e) => set("working_url", e.target.value)} placeholder="https://" />
              </Field>
              <Field label="Client Approval">
                <select className={inputCls} value={form.approval_status} onChange={(e) => set("approval_status", e.target.value)}>
                  {APPROVAL_STATES.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </Field>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-white/10">
            <button data-testid="task-form-reset" onClick={reset} className="text-sm font-mono uppercase tracking-wider border border-white/15 text-zinc-300 hover:bg-white/5 px-4 py-2 rounded-sm transition-colors">
              Reset
            </button>
            <button data-testid="task-form-submit" onClick={submit} disabled={saving} className="text-sm bg-white text-black font-medium px-5 py-2 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98] disabled:opacity-60">
              {saving ? "Creating…" : "Create & Assign Task"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
